import { useId, useRef, type ElementRef } from "react";
import { InputNumber, Select } from "antd";
import { AccountButton } from "./AccountUI.tsx";
import { PlatformAdminIcon } from "../../../features/platform-admin/PlatformAdminIcon.tsx";
import { PendingRing } from "../LoadingRing.jsx";
import { t, localizeDigits, parseLocalizedNumber } from "../../../features/store-language/i18n.ts";
import PortalDialog from "../PortalDialog.tsx";
import "./account-item-picker-dialog.css";

type ItemOption = { value: number; label: string };

export default function AccountItemPickerDialog({ open, title, itemLabel, placeholder,
  options, selected, quantity, maxQuantity, loading, failed, disabled,
  onSearch, onSelect, onQuantity, onAdd, onClose, onRetry }: {
  open: boolean;
  title: string;
  itemLabel: string;
  placeholder: string;
  options: ItemOption[];
  selected?: ItemOption;
  quantity: number | null;
  maxQuantity: number;
  loading: boolean;
  failed: boolean;
  disabled: boolean;
  onSearch: (value: string) => void;
  onSelect: (id?: number) => void;
  onQuantity: (value: number | null) => void;
  onAdd: () => void;
  onClose: () => void;
  onRetry: () => void;
}) {
  const id = useId();
  const selectRef = useRef<ElementRef<typeof Select>>(null);
  const dropdownOpen = useRef(false);
  return (
    <PortalDialog
      open={open}
      width="calc(var(--raj-content-width-compact) + var(--raj-space-16) * 2)"
      className="account-item-picker-dialog"
      onClose={onClose}
      afterOpenChange={function (visible) {
        if (visible) selectRef.current?.focus();
        else dropdownOpen.current = false;
      }}
      title={title}
      footer={(
        <div className="raj-dialog__actions">
          <AccountButton
            type="primary"
            disabled={!selected || loading || failed || disabled}
            onClick={onAdd}
          >
            {t("افزودن")}
          </AccountButton>
          <AccountButton onClick={onClose}>
            {t("انصراف")}
          </AccountButton>
        </div>
      )}
    >
      <div className="account-item-picker-dialog__fields">
        <div className="account-item-picker-dialog__field">
          <label htmlFor={`${id}-item`}>
            {itemLabel}
            <span className="account-item-picker-dialog__required" aria-hidden="true">*</span>
          </label>
          <Select
            ref={selectRef}
            id={`${id}-item`}
            aria-required="true"
            aria-busy={loading}
            showSearch
            allowClear
            labelInValue
            filterOption={false}
            virtual={false}
            value={selected}
            disabled={disabled}
            placeholder={placeholder}
            options={loading ? [] : options}
            onSearch={onSearch}
            onDropdownVisibleChange={function (visible) { dropdownOpen.current = visible; }}
            onInputKeyDown={function (event) {
              // Select consumes Escape; close the dialog only when its popup is already closed.
              if (event.key === "Escape" && !dropdownOpen.current) onClose();
            }}
            onChange={function (option: ItemOption | undefined) { onSelect(option?.value); }}
            popupClassName="account-item-picker-popup"
            getPopupContainer={function (trigger: HTMLElement) { return trigger.closest<HTMLElement>(".ant-modal") || trigger.parentElement || trigger; }}
            suffixIcon={loading ? <PendingRing /> : <PlatformAdminIcon name="search" size={16} />}
            notFoundContent={(
              <div
                className="account-item-picker-dialog__result"
                role="status"
                aria-live="polite"
              >
                {loading ? <PendingRing /> : null}
                <span>
                  {loading ? t("در حال جست‌وجوی کالا…") : failed ? t("دریافت کالاها انجام نشد.") : t("داده‌ای یافت نشد")}
                </span>
              </div>
            )}
          />
        </div>
        <div className="account-item-picker-dialog__field account-item-picker-dialog__quantity">
          <label htmlFor={`${id}-quantity`}>
            {t("تعداد")}
            <span className="account-item-picker-dialog__required" aria-hidden="true">*</span>
          </label>
          <InputNumber<number>
            id={`${id}-quantity`}
            aria-required="true"
            disabled={disabled}
            controls={false}
            precision={0}
            value={quantity}
            formatter={localizeDigits}
            parser={parseLocalizedNumber}
            min={1}
            max={maxQuantity}
            onChange={onQuantity}
          />
        </div>
      </div>
      {failed ? (
        <AccountButton
          loading={loading}
          disabled={loading || disabled}
          onClick={onRetry}
        >
          {t("تازه‌سازی")}
        </AccountButton>
      ) : null}
    </PortalDialog>
  );
}
